import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import { Button } from '@/components/ui/button';
import { Bell, BellOff } from 'lucide-react';
import { NotificationSubscriptionModal } from './NotificationSubscriptionModal';

interface NotificationSubscriptionButtonProps {
  entityType: 'candidate' | 'job' | 'company' | 'match';
  entityId: string;
  entityName: string;
  variant?: 'default' | 'outline' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'icon';
}

export const NotificationSubscriptionButton: React.FC<NotificationSubscriptionButtonProps> = ({
  entityType,
  entityId,
  entityName,
  variant = 'outline',
  size = 'icon'
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [loading, setLoading] = useState(true);

  // Check if user has a subscription for this entity
  useEffect(() => {
    if (user) {
      checkSubscription();
    }
  }, [user, entityType, entityId]);

  const checkSubscription = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('notification_subscriptions')
        .select('id')
        .eq('user_id', user?.id)
        .eq('entity_type', entityType)
        .eq('entity_id', entityId)
        .maybeSingle();

      if (error) {
        console.error('Error checking subscription:', error);
        return;
      }

      setHasSubscription(!!data);
    } catch (error) {
      console.error('Error checking subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    // Refresh subscription status after modal closes
    checkSubscription();
  };

  if (loading) {
    return (
      <Button
        variant={variant}
        size={size}
        disabled
        className="animate-pulse"
      >
        <Bell className="h-4 w-4" />
      </Button>
    );
  }

  return (
    <>
      <Button
        variant={variant}
        size={size}
        onClick={handleClick}
        title={hasSubscription 
          ? t('notifications.subscription.manage', 'Manage notification settings')
          : t('notifications.subscription.setup', 'Set up notifications')
        }
        className={hasSubscription ? 'text-blue-500' : ''}
      >
        {hasSubscription ? (
          <Bell className="h-4 w-4" />
        ) : (
          <BellOff className="h-4 w-4" />
        )}
      </Button>

      <NotificationSubscriptionModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        entityType={entityType}
        entityId={entityId}
        entityName={entityName}
      />
    </>
  );
}; 